import { normalizeAuthToken } from '../utils/helpers';

function buildAuthConfig(token) {
  return {
    headers: {
      Authorization: normalizeAuthToken(token),
    },
  };
}

export async function fetchMemberProfile(client, token, userId) {
  const response = await client.get(
    `${process.env.VUE_APP_API_URL}/profile/${userId}`,
    buildAuthConfig(token),
  );
  return { profile: response.data.profile, viewerRole: response.data.viewerRole || 'public' };
}

export async function searchPsaMembers(client, token, query) {
  const response = await client.get(
    `${process.env.VUE_APP_API_URL}/profile/psa-search`,
    {
      ...buildAuthConfig(token),
      params: { q: query },
    },
  );
  return response.data.results;
}

export async function savePsaMapping(client, token, userId, psaMemberId) {
  const response = await client.patch(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/psa-mapping`,
    { psaMemberId },
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function patchMemberProfile(client, token, userId, fields) {
  const response = await client.patch(
    `${process.env.VUE_APP_API_URL}/profile/${userId}`,
    fields,
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function fetchWorkRecord(client, token, userId, { from, to } = {}) {
  const response = await client.get(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/work-record`,
    {
      ...buildAuthConfig(token),
      params: { from, to },
    },
  );
  return response.data.workRecord;
}

export async function addProfileSkill(client, token, userId, name) {
  const response = await client.post(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/skills`,
    { name },
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function removeProfileSkill(client, token, userId, skillId) {
  const response = await client.delete(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/skills/${skillId}`,
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function addProfileCertification(client, token, userId, fields) {
  const response = await client.post(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/certifications`,
    fields,
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function updateProfileCertification(client, token, userId, certId, fields) {
  const response = await client.patch(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/certifications/${certId}`,
    fields,
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function removeProfileCertification(client, token, userId, certId) {
  const response = await client.delete(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/certifications/${certId}`,
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function addProfileGoal(client, token, userId, text) {
  const response = await client.post(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/goals`,
    { text },
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function updateProfileGoal(client, token, userId, goalId, text) {
  const response = await client.patch(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/goals/${goalId}`,
    { text },
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function removeProfileGoal(client, token, userId, goalId) {
  const response = await client.delete(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/goals/${goalId}`,
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function stream1on1Prep(token, userId, { from, to, template } = {}, { onStatus, onFinal, onError } = {}) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (template) params.set('template', template);
  const url = `${process.env.VUE_APP_API_URL}/profile/${userId}/prep?${params}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: token },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    const error = new Error(err?.message || 'Failed to start 1:1 Prep.');
    error.status = response.status;
    throw error;
  }

  if (!response.body) throw new Error('Streaming response body unavailable.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload = null;
  let done = false;

  while (!done) {
    const { value, done: readerDone } = await reader.read();
    if (readerDone) { done = true; continue; }

    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() || '';

    for (const chunk of chunks) {
      if (!chunk.trim()) continue;
      let parsed = null;
      for (const line of chunk.split('\n')) {
        if (line.startsWith('data: ')) {
          try { parsed = JSON.parse(line.slice(6)); } catch { /* skip */ }
        }
      }
      if (!parsed) continue;

      const eventType = String(parsed?.type || parsed?.event || '').trim();
      if (eventType === 'status' && typeof onStatus === 'function') {
        onStatus(parsed?.message || parsed?.status?.message || '');
      } else if (eventType === 'final') {
        finalPayload = parsed?.payload || parsed?.data || parsed;
        if (typeof onFinal === 'function') onFinal(finalPayload);
      } else if (eventType === 'error') {
        const msg = parsed?.message || 'Generation failed.';
        if (typeof onError === 'function') onError(new Error(msg));
        return null;
      }
    }
  }

  return finalPayload;
}

export async function fetchAlignment(client, token, userId, { from, to } = {}) {
  const response = await client.get(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/alignment`,
    {
      ...buildAuthConfig(token),
      params: { from, to },
    },
  );
  return response.data.alignment;
}

export async function savePublicSettings(client, token, userId, { publicEnabled, visibleFields }) {
  const response = await client.patch(
    `${process.env.VUE_APP_API_URL}/profile/${userId}/public-settings`,
    { publicEnabled, visibleFields },
    buildAuthConfig(token),
  );
  return response.data.settings;
}

export async function setupProfile(client, token, userId, { role, displayName, title }) {
  const response = await client.post(
    `${process.env.VUE_APP_API_URL}/profile/${userId}`,
    { role, displayName, title },
    buildAuthConfig(token),
  );
  return response.data.profile;
}

export async function fetchCompanyName(client, token, companyId) {
  const response = await client.get(
    `${process.env.VUE_APP_API_URL}/companies/${companyId}`,
    buildAuthConfig(token),
  );
  return response.data.company?.name || null;
}
